import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MathJaxContext } from 'better-react-mathjax';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';

import AboutSources from './AboutSections/AboutSources';
import AboutAttributes from './AboutSections/Attributes/AboutAttributes';
import AboutGeneTypes from './AboutSections/AboutGeneTypes';
import AboutVariantTypes from './AboutSections/Attributes/Variants/VariantTypes';
import AboutSchema from './AboutSections/AboutSchema';
import AboutMetrics from './AboutSections/Metrics/AboutMetrics';
import AboutOntology from './AboutSections/AboutOntology';
import AboutCiteUs from './AboutSections/AboutCiteUs';
import AboutSidebar from './components/elements/AboutSidebar';
import LoadingSpinner from '../../global/Loader/LoaderSpinner';

import StatisticsDiagram from './components/statistics/StatisticsDiagram';
import Changelog from './components/statistics/Changelog';
import { StatsTxt } from './components/statistics/StatisticsText';
import { StatsJSON, VersionsArray, VersionInfo } from './types';
import { StatisticsTable } from './components/statistics/StatisticsTables';
import { formatVersion, toHashLabel } from './helpers';

function AboutContent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [statistics, setStats] = useState<StatsJSON | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const hasSection = searchParams.has('section');
    const hasVersion = searchParams.has('version');

    if (!hasSection && !hasVersion) {
      navigate(`${location.pathname}?section=gdaStatistics`, { replace: true });
    }
  }, [searchParams, location.pathname, navigate]);

  // As the JSON is in public directory
  // It is necessary to fetch the import
  useEffect(() => {
    fetch('/stats.json')
      .then(res => {
        if (!res.ok) {
          throw new Error(`Failed to load stats.json: ${res.status}`);
        }
        return res.json();
      })
      .then(data => {
        setStats(data as StatsJSON);
      })
      .catch(error => {
        console.error('❌ Error loading stats.json:', error);
      });
  }, []);

  if (!statistics)
    return (
      <div className="flex justify-center items-center min-h-64">
        <LoadingSpinner />
      </div>
    );

  const currentVersion = statistics.current;
  const section = searchParams.get('section');
  const versionParam = searchParams.get('version');

  const versionsArray: VersionsArray[] = Object.entries(statistics)
    .filter(([key]) => key !== 'current')
    .map(([key, value]) => {
      if (typeof value === 'string') {
        return null;
      }
      return { ...(value as VersionInfo), version: key };
    })
    .filter((v): v is VersionsArray => v !== null);

  const activeVersionObj = (() => {
    if (versionParam) {
      return versionsArray.find(v => toHashLabel(v.version as string) === versionParam);
    }
    return versionsArray.find(v => v.version === currentVersion);
  })();

  const showVersionDetails = versionParam && activeVersionObj;

  return (
    <MathJaxContext>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="sr-only">Information about database statistics, attributes and metrics.</h1>
        {/* Responsive sidebar */}
        <div className="lg:hidden mb-4">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors mt-4 lg:mt-0"
          >
            <span className="font-medium text-gray-900">Navigation Menu</span>
            {sidebarOpen ? (
              <ChevronUpIcon className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronDownIcon className="h-5 w-5 text-gray-500" />
            )}
          </button>
        </div>

        <div className="flex flex-col lg:flex-row lg:gap-8 border-b border-gray-200 pt-3 pb-4">
          {/* Mobile sidebar */}
          <div
            className={`w-full lg:w-1/4 lg:flex-shrink-0 mb-6 lg:mb-0 lg:block ${sidebarOpen ? 'block' : 'hidden'}`}
          >
            <div className="lg:sticky lg:top-4">
              <div className="lg:border-0 rounded-lg lg:rounded-none p-2 lg:p-0 bg-white lg:bg-transparent">
                <AboutSidebar versionsArray={versionsArray} onClick={() => setSidebarOpen(!sidebarOpen)} />
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="w-full lg:w-3/4 lg:flex-grow">
            <div className="tab-content" id="dbInfoTabbedPane">
              {showVersionDetails ? (
                <div className="space-y-6">
                  {activeVersionObj.releaseDate && (
                    <h2 className="text-xl font-bold text-gray-900 sm:text-2xl lg:text-3xl">
                      {activeVersionObj.releaseDate}: Release of version{' '}
                      {formatVersion(activeVersionObj.version)} of DISGENET
                    </h2>
                  )}
                  {activeVersionObj && Object.keys(activeVersionObj.statistics).length > 0 && (
                    <StatsTxt version={activeVersionObj.version} statistics={activeVersionObj.statistics} />
                  )}
                  {activeVersionObj && (
                    <Changelog
                      version={activeVersionObj.version}
                      releaseDate={activeVersionObj.releaseDate}
                      statistics={activeVersionObj.statistics}
                      changelog={activeVersionObj.changelog}
                      currentVersion={currentVersion}
                    />
                  )}
                </div>
              ) : (
                <div className="space-y-6">
                  {section === 'gdaStatistics' && (
                    <div className="tab-pane active show" id="gdaStatistics">
                      <h2 className="text-xl font-bold text-gray-900 mb-4 sm:text-2xl sm:mb-6">Statistics</h2>
                      <div className="space-y-6 sm:space-y-8">
                        <StatsTxt
                          statistics={activeVersionObj!.statistics}
                          version={activeVersionObj!.version}
                        />
                        <StatisticsDiagram statistics={activeVersionObj!.statistics} />
                        <StatisticsTable />
                      </div>
                    </div>
                  )}

                  {section === 'gene_type' && (
                    <div className="tab-pane active show" id="gene_type">
                      <AboutGeneTypes />
                    </div>
                  )}

                  {section === 'variant_type' && (
                    <div className="tab-pane active show" id="variant_type">
                      <AboutVariantTypes />
                    </div>
                  )}

                  {section === 'schema' && (
                    <div className="tab-pane active show" id="schema">
                      <AboutSchema />
                    </div>
                  )}

                  {section === 'sources' && (
                    <div className="tab-pane active show" id="sources">
                      <AboutSources />
                    </div>
                  )}

                  {section === 'metrics' && (
                    <div className="tab-pane active show" id="metrics">
                      <AboutMetrics />
                    </div>
                  )}

                  {section === 'attributes' && (
                    <div className="tab-pane active show" id="attributes">
                      <AboutAttributes />
                    </div>
                  )}

                  {section === 'assoctypeont' && (
                    <div className="tab-pane active show" id="assoctypeont">
                      <AboutOntology />
                    </div>
                  )}

                  {section === 'cite_us' && (
                    <div className="tab-pane active show" id="cite_us">
                      <AboutCiteUs />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </MathJaxContext>
  );
}

export default AboutContent;
